<template>
  <v-dialog
    :model-value="modelValue"
    max-width="760px"
    persistent
    scrollable
    data-testid="tpsv2-import-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="tpsv2-sub tpsv2-imp">
      <div class="tpsv2-head tpsv2-head--sub bg-primary text-white">
        <div class="tpsv2-head-mark"><v-icon size="19">mdi-import</v-icon></div>
        <div class="tpsv2-head-text">
          <h2 class="tpsv2-head-title">นำเข้าการตั้งค่าจากไฟล์</h2>
          <p class="tpsv2-head-sub">
            ไฟล์ที่ได้จากปุ่ม “ส่งออกเป็นไฟล์” ของเครื่องอื่น — ตรวจก่อน แล้วค่อยนำเข้า
          </p>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          color="white"
          aria-label="ปิดหน้าต่างนำเข้า"
          data-testid="tpsv2-import-close-btn"
          @click="close"
        />
      </div>

      <v-card-text class="tpsv2-impbody">
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert"
          data-testid="tpsv2-import-error"
        >
          {{ error }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          density="compact"
          class="tpsv2-alert"
          data-testid="tpsv2-import-hint"
        >
          ไฟล์นี้จะถูกสร้างเป็น <strong>เวอร์ชันใหม่</strong> และเป็น “ไม่ได้ใช้งาน” เสมอ —
          เวอร์ชันที่ใช้งานอยู่จะไม่ถูกแตะ
        </v-alert>

        <!-- ── 1. เลือกไฟล์ / วาง JSON ─────────────────────────────── -->
        <div class="tpsv2-imp-pickrow">
          <v-btn
            variant="outlined"
            size="small"
            prepend-icon="mdi-file-upload-outline"
            data-testid="tpsv2-import-pick"
            @click="pickImportFile"
          >
            เลือกไฟล์ .json
          </v-btn>
          <span v-if="fileName" class="tpsv2-muted" data-testid="tpsv2-import-filename">
            {{ fileName }}
          </span>
        </div>
        <input
          ref="importFile"
          type="file"
          accept="application/json,.json"
          class="d-none"
          data-testid="tpsv2-import-file"
          @change="onImportFileChosen"
        >

        <v-textarea
          v-model="importText"
          label="หรือวาง JSON ที่นี่"
          rows="6"
          variant="outlined"
          density="compact"
          hide-details
          data-testid="tpsv2-import-text"
          @update:model-value="onTextInput"
        />

        <v-alert
          v-if="parseError"
          type="error"
          variant="tonal"
          density="compact"
          class="tpsv2-alert"
          data-testid="tpsv2-import-parse-error"
        >
          {{ parseError }}
        </v-alert>

        <!-- ที่มาของไฟล์ (อ่านจากตัวไฟล์เอง — ยังไม่ได้ยิง dry-run) -->
        <v-alert
          v-else-if="sourceLines.length"
          type="info"
          variant="tonal"
          density="compact"
          icon="mdi-file-document-outline"
          class="tpsv2-alert"
          data-testid="tpsv2-import-source"
        >
          <div class="tpsv2-imp-subtitle">ที่มาของไฟล์</div>
          <div v-for="line in sourceLines" :key="line.label" class="tpsv2-imp-line">
            {{ line.label }}: <strong>{{ line.value }}</strong>
          </div>
        </v-alert>

        <!-- ── 2. รหัสเวอร์ชัน / คำอธิบาย ────────────────────────────── -->
        <div class="tpsv2-imp-grid">
          <div class="tpsv2-field">
            <div class="tpsv2-field-head"><span class="tpsv2-field-label">รหัสเวอร์ชันใหม่</span></div>
            <v-text-field
              v-model="versionCode"
              variant="outlined"
              density="compact"
              hide-details="auto"
              prepend-inner-icon="mdi-tag-outline"
              placeholder="เว้นว่าง = ใช้รหัสในไฟล์"
              persistent-placeholder
              aria-label="รหัสเวอร์ชันใหม่"
              data-testid="tpsv2-import-code-input"
              @update:model-value="onCodeInput"
            />
            <p class="tpsv2-hint">ถ้าชนของเดิม ระบบจะต่อท้ายให้ (V1 → V1-2)</p>
          </div>
          <div class="tpsv2-field">
            <div class="tpsv2-field-head"><span class="tpsv2-field-label">คำอธิบาย</span></div>
            <v-text-field
              v-model="description"
              variant="outlined"
              density="compact"
              hide-details="auto"
              prepend-inner-icon="mdi-text-box-outline"
              placeholder="เว้นว่าง = ใช้คำอธิบายในไฟล์"
              persistent-placeholder
              aria-label="คำอธิบายเวอร์ชัน"
              data-testid="tpsv2-import-description-input"
              @update:model-value="onDescriptionInput"
            />
          </div>
        </div>

        <!-- ── 3. ผลการตรวจไฟล์ (dry-run) ───────────────────────────── -->
        <v-card v-if="preview" variant="tonal" class="tpsv2-imp-preview" data-testid="tpsv2-import-preview">
          <div class="tpsv2-imp-previewbody">
            <div class="tpsv2-imp-subtitle">ผลการตรวจไฟล์</div>

            <div class="tpsv2-imp-chips">
              <v-chip size="small" variant="flat" color="primary" prepend-icon="mdi-tag">
                รหัสที่จะได้: {{ preview.versionCode }}
              </v-chip>
              <v-chip size="small" variant="tonal" prepend-icon="mdi-tune-variant">
                {{ settingsKeyCount }} ช่องในไฟล์
              </v-chip>
              <v-chip
                v-if="unresolvedModels.length"
                size="small"
                variant="tonal"
                color="warning"
                prepend-icon="mdi-robot-off-outline"
              >
                โมเดลหาไม่เจอ {{ unresolvedModels.length }} ช่อง
              </v-chip>
            </div>

            <v-alert
              v-if="preview.versionCodeAdjusted"
              type="warning"
              variant="tonal"
              density="compact"
              class="tpsv2-alert"
              data-testid="tpsv2-import-code-adjusted"
            >
              รหัสที่ระบุชนกับเวอร์ชันเดิม — ระบบเปลี่ยนให้เป็น
              <strong>{{ preview.versionCode }}</strong> อัตโนมัติ
            </v-alert>

            <!-- 🔴 The loudest thing on this panel: an unresolved slot leaves that column NULL,
                 which means "use the built-in default", not "keep what the file said". -->
            <v-alert
              v-if="unresolvedModels.length"
              type="warning"
              variant="tonal"
              density="compact"
              icon="mdi-robot-off-outline"
              class="tpsv2-alert"
              data-testid="tpsv2-import-unresolved"
            >
              <div class="tpsv2-imp-subtitle">
                หาโมเดลปลายทางไม่เจอ {{ unresolvedModels.length }} ช่อง — ช่องเหล่านี้จะถูกเว้นว่าง
                (= ใช้ค่าเริ่มต้นในโค้ด) ต้องมาเลือกโมเดลเองหลังนำเข้า
              </div>
              <ul class="tpsv2-imp-list">
                <li v-for="(m, i) in unresolvedModels" :key="`m${i}`">
                  <strong>{{ slotLabel(m.slot) }}</strong> — {{ modelRefText(m) }}
                </li>
              </ul>
            </v-alert>

            <v-alert
              v-if="previewWarnings.length"
              type="warning"
              variant="tonal"
              density="compact"
              class="tpsv2-alert"
              data-testid="tpsv2-import-warnings"
            >
              <div class="tpsv2-imp-subtitle">
                พบข้อควรระวัง {{ previewWarnings.length }} รายการ — นำเข้าต่อได้ แต่ควรอ่านก่อน
              </div>
              <ul class="tpsv2-imp-list">
                <li v-for="(w, i) in previewWarnings" :key="`w${i}`">{{ w }}</li>
              </ul>
            </v-alert>

            <v-alert
              v-if="!previewWarnings.length && !unresolvedModels.length"
              type="success"
              variant="tonal"
              density="compact"
              class="tpsv2-alert"
              data-testid="tpsv2-import-ok"
            >
              ตรวจไฟล์แล้ว ไม่พบปัญหา — นำเข้าได้เลย
            </v-alert>
          </div>
        </v-card>
      </v-card-text>

      <v-divider />
      <v-card-actions class="tpsv2-foot">
        <v-spacer />
        <v-btn
          variant="outlined"
          :disabled="checking || importing"
          data-testid="tpsv2-import-cancel-btn"
          @click="close"
        >
          <v-icon start>mdi-close</v-icon>
          ยกเลิก
        </v-btn>
        <v-btn
          color="primary"
          variant="tonal"
          :disabled="!parsedBundle || importing"
          :loading="checking"
          data-testid="tpsv2-import-check-btn"
          @click="runDryRun"
        >
          <v-icon start>mdi-file-search-outline</v-icon>
          ตรวจไฟล์
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          :disabled="!preview || checking"
          :loading="importing"
          data-testid="tpsv2-import-submit-btn"
          @click="runImport"
        >
          <v-icon start>mdi-import</v-icon>
          นำเข้าเลย
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Import a Tax Planning Settings V2 bundle (`.json` produced by the row's «ส่งออกเป็นไฟล์») as a
 * NEW settings version — the replacement for hand-copying 85 prompt/config columns between
 * Local → Dev → UAT.
 *
 * Contract with the parent (`TaxPlanningSettingsV2Dialog.vue`):
 *   - this dialog reports its own errors inline (`v-alert`), never through a snackbar of its own;
 *   - `imported` fires AFTER the real (non-dry-run) call succeeded, carrying the whole
 *     `ImportTaxSettingsBundleResult`, and this dialog does NOT close itself — the parent decides,
 *     because it is the one that must reload the version list and surface the warnings.
 *     «ยกเลิก» / «X» still emit `update:modelValue: false` normally.
 *
 * Two-step by design: «ตรวจไฟล์» runs the SAME server-side resolve with `dryRun: true` (writes
 * nothing) and its result is what unlocks «นำเข้าเลย»; editing anything afterwards throws the
 * preview away, because a preview that describes older text is worse than no preview.
 *
 * 🔴 A model slot the destination cannot resolve is NOT a failure — the column is left `null`,
 * which means "use the built-in default in the code". That is a real change to how the runtime
 * behaves, so `unresolvedModels` gets its own loud panel rather than being folded into `warnings`.
 *
 * Unlike the Question / Calculate imports there is no destination id: a settings version is not
 * bound to a tax config version, so the file plus an optional code is the whole request.
 */
import { taxPlanningSettingsV2Api } from '@/services/taxPlanningSettingsV2Api';

/** Envelope marker written by the export endpoint — sniffed before anything else is touched. */
const BUNDLE_KIND = 'lhb.taxsettings.v2.bundle';

/** `unresolvedModels[].slot` → the label used on this screen. */
const SLOT_LABEL = {
  main: 'โมเดลหลัก',
  question: 'โมเดลของคำถาม',
  voiceFill: 'โมเดล VoiceFill',
};

export default {
  name: 'TaxSettingsImportV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    /** parent's suggestion for the new code, used until the file supplies its own */
    suggestedCode: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'imported'],
  data() {
    return {
      importText: '',
      fileName: '',
      /** why the pasted/loaded text is not a usable bundle (set from `parsedBundle`) */
      parseError: '',
      /** API-level failure of the dry run / the import itself */
      error: '',
      /** `ImportTaxSettingsBundleResult` of the last dry run — gates «นำเข้าเลย» */
      preview: null,
      versionCode: '',
      description: '',
      /** stop the auto-prefill from the file once the user has typed their own */
      codeTouched: false,
      descriptionTouched: false,
      checking: false,
      importing: false,
    };
  },
  computed: {
    /**
     * The parsed bundle, or null while the text does not hold a usable one. Doubles as the
     * «ตรวจไฟล์» button's enable test — parsing here rather than on submit means a wrong file
     * (a Tax Question export, a Calculate bundle) is caught before the server is called.
     * Sets `parseError` as a side effect so the reason is visible.
     */
    parsedBundle() {
      const raw = (this.importText || '').trim();
      if (!raw) { this.parseError = ''; return null; }
      let bundle;
      try {
        bundle = JSON.parse(raw);
      } catch (e) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ JSON ที่อ่านได้';
        return null;
      }
      if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
        this.parseError = 'ไฟล์นี้ไม่ใช่ไฟล์การตั้งค่า Tax Planning V2';
        return null;
      }
      // Envelope sniff BEFORE anything else — the same check the backend makes, made here so a
      // wrong file never reaches the server at all.
      if (String(bundle.kind || '').toLowerCase() !== BUNDLE_KIND) {
        this.parseError = bundle.kind
          ? `ไฟล์นี้ไม่ใช่ไฟล์การตั้งค่า Tax Planning V2 — ไฟล์นี้เป็น "${bundle.kind}"`
          : 'ไฟล์นี้ไม่ใช่ไฟล์การตั้งค่า Tax Planning V2 — ไม่พบ kind ในไฟล์';
        return null;
      }
      const settings = bundle.settings;
      if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        this.parseError = 'ไฟล์นี้ไม่มีส่วน settings อยู่เลย';
        return null;
      }
      if (!Object.keys(settings).length) {
        this.parseError = 'ไฟล์นี้มี settings ว่างเปล่า';
        return null;
      }
      this.parseError = '';
      return bundle;
    },
    /** How many columns the file carries — the one number that says "this file has content". */
    settingsKeyCount() {
      const b = this.parsedBundle;
      const s = (b && b.settings) || null;
      return s ? Object.keys(s).length : 0;
    },
    /** `source.*` of the loaded file — every blank field is DROPPED, never rendered as "null". */
    sourceLines() {
      const b = this.parsedBundle;
      if (!b) return [];
      const lines = [];
      const push = (label, value) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        lines.push({ label, value: String(value) });
      };
      const src = b.source || {};
      push('เวอร์ชันต้นทาง', src.versionCode);
      push('คำอธิบาย', src.description);
      push('ปีภาษีในไฟล์', b.settings && b.settings.taxYearCode);
      lines.push({ label: 'ในไฟล์', value: `${this.settingsKeyCount} ช่องตั้งค่า` });
      push('ส่งออกเมื่อ', b.exportedAt);
      push('ส่งออกโดย', b.exportedBy);
      return lines;
    },
    previewWarnings() {
      const w = this.preview && this.preview.warnings;
      return Array.isArray(w) ? w : [];
    },
    unresolvedModels() {
      const m = this.preview && this.preview.unresolvedModels;
      return Array.isArray(m) ? m : [];
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetState();
    },
    /** Prefill the code/description from the file, but never over something the user typed. */
    parsedBundle(bundle) {
      const src = (bundle && bundle.source) || {};
      if (!this.codeTouched) {
        this.versionCode = this.normalizeCode(src.versionCode || this.suggestedCode || '');
      }
      if (!this.descriptionTouched) {
        this.description = src.description || '';
      }
    },
  },
  methods: {
    normalizeCode(value) {
      return String(value || '').replace(/\s+/g, '').toUpperCase();
    },
    slotLabel(slot) {
      return SLOT_LABEL[slot] || slot || 'โมเดล';
    },
    /** Whatever the file could tell us about the model that did not resolve here. */
    modelRefText(m) {
      const parts = [];
      if (m && m.botName) parts.push(`บอท ${m.botName}`);
      if (m && m.provider) parts.push(m.provider);
      if (m && m.modelName) parts.push(m.modelName);
      return parts.length ? parts.join(' · ') : 'ไม่มีข้อมูลในไฟล์';
    },
    resetState() {
      this.importText = '';
      this.fileName = '';
      this.parseError = '';
      this.error = '';
      this.preview = null;
      this.versionCode = this.normalizeCode(this.suggestedCode);
      this.description = '';
      this.codeTouched = false;
      this.descriptionTouched = false;
      this.checking = false;
      this.importing = false;
      const el = this.$refs.importFile;
      if (el) el.value = '';
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    pickImportFile() {
      const el = this.$refs.importFile;
      // Clearing FIRST so re-picking the SAME file still fires `change` (the browser skips the
      // event when the value is unchanged).
      if (el) { el.value = ''; el.click(); }
    },
    onImportFileChosen(evt) {
      const file = evt && evt.target && evt.target.files && evt.target.files[0];
      if (!file) return;
      this.fileName = file.name || '';
      this.error = '';
      this.preview = null;
      const reader = new FileReader();
      reader.onload = () => { this.importText = String(reader.result || ''); };
      reader.onerror = () => {
        this.importText = '';
        this.parseError = 'อ่านไฟล์ไม่สำเร็จ';
      };
      reader.readAsText(file);
    },
    /** Any edit invalidates the dry run — the preview must describe the text that is there NOW. */
    onTextInput() {
      this.preview = null;
      this.error = '';
    },
    onCodeInput(value) {
      this.codeTouched = true;
      this.versionCode = this.normalizeCode(value);
      // The code is part of what the dry run decided (collision → `V1-2`), so re-check is required.
      this.preview = null;
    },
    onDescriptionInput() {
      this.descriptionTouched = true;
      this.preview = null;
    },
    /** Shared payload of both calls — the only difference between them is `dryRun`. */
    buildRequest(dryRun) {
      return {
        bundle: this.parsedBundle,
        versionCode: this.normalizeCode(this.versionCode) || null,
        description: (this.description || '').trim() || null,
        dryRun,
      };
    },
    async runDryRun() {
      if (!this.parsedBundle || this.checking || this.importing) return;
      this.checking = true;
      this.error = '';
      this.preview = null;
      try {
        this.preview = await taxPlanningSettingsV2Api.importBundle(this.buildRequest(true));
      } catch (e) {
        this.error = (e && e.message) || 'ตรวจไฟล์ไม่สำเร็จ';
      } finally {
        this.checking = false;
      }
    },
    async runImport() {
      if (!this.preview || this.checking || this.importing) return;
      this.importing = true;
      this.error = '';
      try {
        const result = await taxPlanningSettingsV2Api.importBundle(this.buildRequest(false));
        // Deliberately NOT closing here — the parent reloads the list, shows the warnings and
        // decides when this dialog goes away.
        this.$emit('imported', result);
      } catch (e) {
        this.error = (e && e.message) || 'นำเข้าการตั้งค่าไม่สำเร็จ';
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>

<style scoped>
/**
 * 🔴 The `--tps-*` ramp is declared HERE as well as in `TaxPlanningSettingsV2Dialog.vue`: this
 * dialog is teleported into its own overlay, so it is not a descendant of `.tpsv2` and would
 * otherwise resolve every var to nothing (no borders, no text colour).
 */
.tpsv2-sub {
  --tps-ink: rgba(var(--v-theme-on-surface), 0.94);
  --tps-ink-2: rgba(var(--v-theme-on-surface), 0.75);
  --tps-ink-3: rgba(var(--v-theme-on-surface), 0.66);
  --tps-line: rgba(var(--v-theme-on-surface), 0.1);
  --tps-line-soft: rgba(var(--v-theme-on-surface), 0.06);
  --tps-panel: rgba(var(--v-theme-on-surface), 0.02);
  --tps-panel-2: rgba(var(--v-theme-on-surface), 0.045);
  --tps-gutter: 20px;
  display: flex;
  flex-direction: column;
}

.tpsv2-head {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px var(--tps-gutter);
}

.tpsv2-head-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
}

.tpsv2-head-text { flex: 1 1 auto; min-width: 0; }

/* The global reset zeroes heading weight, so titles set their own. */
.tpsv2-head-title { font-size: 1.05rem; font-weight: 600; line-height: 1.35; }
.tpsv2-head-sub { font-size: 0.75rem; line-height: 1.4; opacity: 0.82; }

.tpsv2-impbody {
  padding: 18px var(--tps-gutter) 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.tpsv2-imp-pickrow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: -4px;
}

.tpsv2-imp-grid {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 14px;
}

@media (max-width: 600px) {
  .tpsv2-imp-grid { grid-template-columns: minmax(0, 1fr); }
}

.tpsv2-alert { font-size: 0.8rem; line-height: 1.6; }
.tpsv2-muted { font-size: 0.8rem; color: var(--tps-ink-3); word-break: break-all; }
.tpsv2-hint { font-size: 0.72rem; line-height: 1.5; color: var(--tps-ink-3); margin-top: 4px; }

.tpsv2-field-head { margin-bottom: 6px; }
.tpsv2-field-label { font-size: 0.8rem; font-weight: 600; color: var(--tps-ink); }

.tpsv2-imp-preview { border-radius: 10px; }
.tpsv2-imp-previewbody {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tpsv2-imp-subtitle { font-size: 0.82rem; font-weight: 600; line-height: 1.5; }
.tpsv2-imp-line { font-size: 0.75rem; line-height: 1.6; }

.tpsv2-imp-chips {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.tpsv2-imp-list {
  margin: 4px 0 0;
  padding-left: 18px;
  font-size: 0.75rem;
  line-height: 1.65;
}

.tpsv2-foot {
  flex: none;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px var(--tps-gutter);
}
</style>
